import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAxios from "./useAxios";
import { classifyPreviewStreamResult } from "../utils/previewStreamOutcome";
import usePfqAxios from "./usePfqAxios";
import { toaster } from "../utils/toast";
import Constants from "../Constants";
import useCaseResultsHistory from "./useCaseResultsHistory";
import {
  buildEditCommentsRequestPayload,
  buildSectionComments,
  buildSectionDefinitions,
  buildSuggestedEditPayload,
  getCommentIdForSection,
  isSectionPresentInResultRequests,
  getUpdatedEditCommentsFromResponse,
  mapCreateRequestStatusToUiState,
  normalizeString,
  SECTION_STATUS_POLL_MS,
} from "@demandproai/shared";
import useDemandStudioReturnRoute from "./useDemandStudioReturnRoute";
import useStudioRegeneration, { toastRegenerationResult, useSharedRegenerationOutcome } from "./useStudioRegeneration";

const notifyRequestFailure = ({ message, error }) => {
  toaster({ message: error?.message || message, success: false });
};

const isSectionDirtyByMap = (section, dirtySectionIds) => {
  return Boolean(dirtySectionIds?.[section?.id]);
};

// a refetch triggered by something else (an autosaved manual edit, a socket notification) must not
// wipe comment text the user is still typing, so dirty sections keep their draft on rehydration
const keepUnsavedComments = ({ hydratedSections, previousSections, dirtySectionIds }) => {
  const previousSectionById = new Map(
    (previousSections || []).map((section) => [section.id, section])
  );
  return hydratedSections.map((section) => {
    if (!isSectionDirtyByMap(section, dirtySectionIds)) return section;
    const previousSection = previousSectionById.get(section.id);
    if (!previousSection) return section;
    return {
      ...section,
      comment: previousSection.comment,
      lastEditedAt: previousSection.lastEditedAt,
    };
  });
};

const resolveSectionUiState = ({
  section,
  previousUiStateBySectionId,
  latestStatusByCommentId,
  isSectionDirty,
  regeneratedSectionIds,
}) => {
  if (isSectionDirty(section)) {
    return previousUiStateBySectionId[section.id] ?? "idle";
  }
  if (!section.commentId) {
    return "idle";
  }
  const statusEntry = latestStatusByCommentId[String(section.commentId)];
  const previousUiState = previousUiStateBySectionId[section.id];
  // keep an in-flight loader alive when the status feed lags behind (e.g. a second
  // submission re-queries before this section's request is reflected in the map)
  if (statusEntry === undefined && previousUiState === "loading") {
    return "loading";
  }
  const uiState = mapCreateRequestStatusToUiState(statusEntry?.status);
  const shouldResetToIdle =
    !regeneratedSectionIds[section.id] && (uiState === "success" || uiState === "failed");
  return shouldResetToIdle ? "idle" : uiState;
};

const mergeSectionUiStateFromStatuses = ({
  previousUiStateBySectionId,
  sections,
  latestStatusByCommentId,
  isSectionDirty,
  regeneratedSectionIds,
}) => {
  const nextUiStateBySectionId = { ...previousUiStateBySectionId };
  const settledSections = [];
  let hasChanges = false;
  sections.forEach((section) => {
    const previousValue = previousUiStateBySectionId[section.id];
    const nextValue = resolveSectionUiState({
      section,
      previousUiStateBySectionId,
      latestStatusByCommentId,
      isSectionDirty,
      regeneratedSectionIds,
    });
    if (previousValue === nextValue) {
      return;
    }
    hasChanges = true;
    nextUiStateBySectionId[section.id] = nextValue;
    if (nextValue === "success" || nextValue === "failed") {
      settledSections.push({
        uiState: nextValue,
        errorMessage: latestStatusByCommentId[String(section.commentId)]?.errorMessage,
      });
    }
  });
  return { hasChanges, nextUiStateBySectionId, settledSections };
};

const SECTION_REGEN_GENERIC_FAILURE = "Section regeneration failed.";
const SECTION_REGEN_GENERIC_SUCCESS = "Section regenerated.";

const buildSettledGroupMessage = ({ count, verb }) => `${count} sections ${verb}.`;

const notifySectionsForOutcome = ({ sections, singleMessage, groupVerb, notify }) => {
  if (sections.length === 1) {
    notify(singleMessage(sections[0]));
  } else if (sections.length > 1) {
    notify(buildSettledGroupMessage({ count: sections.length, verb: groupVerb }));
  }
};

/**
 * Toasts settled sections grouped by outcome, one toast per outcome per poll tick.
 * @param {Array<{ uiState: "success" | "failed", errorMessage?: string }>} settledSections
 */
export const notifySettledSections = (settledSections) => {
  const successSections = settledSections.filter(({ uiState }) => uiState === "success");
  const failedSections = settledSections.filter(({ uiState }) => uiState === "failed");

  notifySectionsForOutcome({
    sections: successSections,
    singleMessage: () => SECTION_REGEN_GENERIC_SUCCESS,
    groupVerb: "regenerated",
    notify: (message) => toaster({ message, success: true }),
  });

  notifySectionsForOutcome({
    sections: failedSections,
    singleMessage: (section) => section.errorMessage || SECTION_REGEN_GENERIC_FAILURE,
    groupVerb: "failed to regenerate",
    notify: (message) => notifyRequestFailure({ message }),
  });
};

const DEFAULT_REGENERATE_CONFIG = {
  startMessage: "Revisions saved. Regenerating demand letters...",
  failureMessage: "Failed to start demand letter regeneration.",
  successMessage: "Demand letters regenerated.",
  failureNotification: "Demand letter regeneration failed.",
  previewFailureMessage: "Failed to start preview generation."
};

const DEFAULT_SUGGESTED_EDITS_SETTLED_SIGNAL = { caseId: null };

const useDemandStudioData = ({
  isOpen,
  caseId,
  regenerateConfig,
  customSectionDefinitions,
  shouldRegenerateOnUnmount = true,
}) => {
  const resolvedRegenerateConfig = useMemo(
    () => ({ ...DEFAULT_REGENERATE_CONFIG, ...(regenerateConfig || {}) }),
    [regenerateConfig]
  );
  const { putData, isLoading } = useAxios();
  const { postData: postPfqData } = usePfqAxios();
  const {
    caseData: caseDetails,
    createRequests: resultRequests,
    fetchCaseDetails,
    hasMoreHistory,
    loadOlderUntil,
  } = useCaseResultsHistory({ caseId });
  const [isInitialLoadPending, setIsInitialLoadPending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFinalizingExit, setIsFinalizingExit] = useState(false);
  const [sectionUiStateById, setSectionUiStateByIdRaw] = useState({});
  const sectionUiStateRef = useRef({});
  const setSectionUiStateById = useCallback((updater) => {
    setSectionUiStateByIdRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      sectionUiStateRef.current = next;
      return next;
    });
  }, []);
  const [suggestedEditsSettledSignal, setSuggestedEditsSettledSignal] = useState(
    DEFAULT_SUGGESTED_EDITS_SETTLED_SIGNAL
  );
  const dirtySectionIdsRef = useRef({});
  const regeneratedSectionIdsRef = useRef({});
  const [showPreview, setShowPreview] = useState(false);
  const previousIsOpenRef = useRef(false);
  const [sectionComments, setSectionComments] = useState([]);
  const sectionCommentsRef = useRef([]);
  sectionCommentsRef.current = sectionComments;

  const {
    regenerate,
    isRegenerating
  } = useStudioRegeneration({
    caseId,
    endpoint: Constants.ApiUrl.regenerateDemandLetters,
  });

  const { regenerateSilently, claimOutcomeHandling } = useSharedRegenerationOutcome(regenerate);

  // fallback regeneration for hard navigations that bypass the guarded close
  const regenerateRef = useRef(regenerateSilently);
  regenerateRef.current = regenerateSilently;
  // hosts that own a narrower regeneration opt out, so closing does not race two rebuilds on the same case
  const shouldRegenerateOnUnmountRef = useRef(shouldRegenerateOnUnmount);
  shouldRegenerateOnUnmountRef.current = shouldRegenerateOnUnmount;
  useEffect(() => () => {
    if (!shouldRegenerateOnUnmountRef.current) return;
    regenerateRef.current();
  }, []);

  useEffect(() => {
    fetchCaseDetails();
  }, [fetchCaseDetails]);

  const isSectionDirty = useCallback((section) => {
    return isSectionDirtyByMap(section, dirtySectionIdsRef.current);
  }, []);

  const sectionDefinitions = useMemo(
    () => {
      return customSectionDefinitions ?? buildSectionDefinitions(caseDetails, resultRequests)
    },
    [
      customSectionDefinitions,
      caseDetails,
      resultRequests,
    ]
  );

  const applicableSectionIds = useMemo(() => {
    const applicable = new Set();
    sectionDefinitions.forEach((section) => {
      if (isSectionPresentInResultRequests(resultRequests, section)) {
        applicable.add(section.id);
      }
    });
    return applicable;
  }, [resultRequests, sectionDefinitions]);

  const syncSectionComments = useCallback((nextComments) => {
    sectionCommentsRef.current = nextComments;
    setSectionComments(nextComments);
  }, []);

  const hydrateSectionComments = useCallback(() => {
    const nextComments = buildSectionComments(sectionDefinitions, caseDetails?.editComments || []);
    syncSectionComments(
      keepUnsavedComments({
        hydratedSections: nextComments,
        previousSections: sectionCommentsRef.current,
        dirtySectionIds: dirtySectionIdsRef.current,
      })
    );
  }, [sectionDefinitions, caseDetails, syncSectionComments]);

  const resetModalTransientState = useCallback(() => {
    setSectionUiStateById({});
    dirtySectionIdsRef.current = {};
    regeneratedSectionIdsRef.current = {};
  }, []);

  const initializeModalOnOpen = useCallback(() => {
    resetModalTransientState();
    hydrateSectionComments();
  }, [resetModalTransientState, hydrateSectionComments]);

  useEffect(() => {
    let isCurrent = true;
    const didJustOpen = isOpen && !previousIsOpenRef.current;
    if (didJustOpen) {
      setIsInitialLoadPending(true);
      Promise.resolve(initializeModalOnOpen()).finally(() => {
        if (isCurrent) {
          setIsInitialLoadPending(false);
        }
      });
    } else if (!isOpen) {
      setIsInitialLoadPending(false);
    }
    previousIsOpenRef.current = isOpen;
    return () => {
      isCurrent = false;
    };
  }, [isOpen, initializeModalOnOpen]);

  useEffect(() => {
    if (!isOpen || !caseDetails) return;
    if (Object.keys(regeneratedSectionIdsRef.current).length > 0) return;
    hydrateSectionComments();
  }, [isOpen, caseDetails, hydrateSectionComments]);

  const mergeSectionUiWithStatusResponse = useCallback((statusResponse, sectionsOverride) => {
    const latestStatusByCommentId = statusResponse?.latestStatusByCommentId || {};
    const regeneratedIds = regeneratedSectionIdsRef.current;
    const sections = sectionsOverride ?? sectionCommentsRef.current;
    const { hasChanges, nextUiStateBySectionId, settledSections } = mergeSectionUiStateFromStatuses({
      previousUiStateBySectionId: sectionUiStateRef.current,
      sections,
      latestStatusByCommentId,
      isSectionDirty,
      regeneratedSectionIds: regeneratedIds,
    });
    if (hasChanges) {
      setSectionUiStateById(nextUiStateBySectionId);
    }
    if (settledSections.length > 0) {
      notifySettledSections(settledSections);
      fetchCaseDetails();
      setSuggestedEditsSettledSignal({ caseId });
    }
  }, [isSectionDirty, setSectionUiStateById, fetchCaseDetails, caseId]);

  const syncSectionUiStatesWithCreateRequests = useCallback(
    async (sectionsOverride) => {
      if (!isOpen || !caseId) {
        return;
      }
      try {
        const response = await postPfqData(
          Constants.ApiUrl.suggestedEditRequestStatuses,
          JSON.stringify({ caseId })
        );
        if (!response?.success) {
          notifyRequestFailure({
            message: response?.message || "Failed to load section regeneration statuses.",
          });
          return;
        }
        mergeSectionUiWithStatusResponse(response, sectionsOverride);
      } catch (error) {
        notifyRequestFailure({
          message: "Failed to load section regeneration statuses.",
          error,
        });
      }
    },
    [isOpen, caseId, postPfqData, mergeSectionUiWithStatusResponse, notifyRequestFailure]
  );

  useEffect(() => {
    if (!isOpen || !caseDetails) return;
    syncSectionUiStatesWithCreateRequests();
  }, [isOpen, caseDetails, syncSectionUiStatesWithCreateRequests]);

  useEffect(() => {
    if (!isOpen) return;
    const hasLoadingSections = Object.values(sectionUiStateById).some((s) => s === "loading");
    if (!hasLoadingSections) return;
    const interval = setInterval(() => {
      syncSectionUiStatesWithCreateRequests();
    }, SECTION_STATUS_POLL_MS);
    return () => clearInterval(interval);
  }, [isOpen, sectionUiStateById, syncSectionUiStatesWithCreateRequests]);

  const updateSectionComment = useCallback((sectionId, value) => {
    dirtySectionIdsRef.current = { ...dirtySectionIdsRef.current, [sectionId]: true };
    setSectionUiStateById((prev) => ({ ...prev, [sectionId]: "idle" }));
    setSectionComments((prev) => {
      const next = prev.map((section) =>
        section.id === sectionId
          ? { ...section, comment: value, lastEditedAt: new Date().toISOString() }
          : section
      );
      sectionCommentsRef.current = next;
      return next;
    });
  }, []);

  const persistComments = useCallback(async () => {
    if (!caseId) {
      toaster({ message: "Case is required to save revisions.", success: false });
      return null;
    }

    try {
      console.log("Persisting section comments ref ", sectionCommentsRef.current);
      const requestPayload = buildEditCommentsRequestPayload({
        caseId,
        sections: sectionCommentsRef.current,
      });
      console.log("sending request payload ", requestPayload);

      const response = await putData(Constants.ApiUrl.case.editComments, requestPayload);

      if (!response?.success) {
        toaster({ message: response?.message || "Failed to save revisions.", success: false });
        return null;
      }

      const updatedEditComments = getUpdatedEditCommentsFromResponse(response);
      if (updatedEditComments) {
        const next = buildSectionComments(sectionDefinitions, updatedEditComments);
        syncSectionComments(next);
      }
      return {
        response,
        updatedEditComments,
      };
    } catch (error) {
      notifyRequestFailure({ message: "Failed to save revisions.", error });
      return null;
    }
  }, [caseId, putData, sectionDefinitions, syncSectionComments, notifyRequestFailure]);

  const clearAllComments = useCallback(async ({ sectionIds } = {}) => {
    const targeted = sectionIds?.length ? sectionComments.filter((s) => sectionIds.includes(s.id)) : sectionComments;
    const cleared = sectionComments.map((section) =>
      targeted.some((t) => t.id === section.id) ? { ...section, comment: "" } : section
    );
    sectionCommentsRef.current = cleared;
    dirtySectionIdsRef.current = targeted.reduce(
      (acc, section) => {
        acc[section.id] = true;
        return acc;
      },
      { ...dirtySectionIdsRef.current }
    );
    setSectionUiStateById((prev) =>
      targeted.reduce((acc, section) => {
        acc[section.id] = "idle";
        return acc;
      }, { ...prev })
    );
    setSectionComments(cleared);
    setIsSaving(true);
    try {
      const persisted = await persistComments();
      if (persisted) {
        dirtySectionIdsRef.current = {};
        toaster({ message: "All comments deleted.", success: true });
      }
    } finally {
      setIsSaving(false);
    }
  }, [sectionComments, persistComments]);

  const { returnToPreviousRoute } = useDemandStudioReturnRoute();

  // hold the user in the studio until any pending regeneration finishes, then return
  const onClose = useCallback(async () => {
    try {
      setIsFinalizingExit(true);
      const regenerationPromise = regenerate();
      claimOutcomeHandling(regenerationPromise);
      toastRegenerationResult(await regenerationPromise);
    } finally {
      setIsFinalizingExit(false);
      returnToPreviousRoute();
    }
  }, [regenerate, returnToPreviousRoute]);

  const handleSave = useCallback(async () => {
    if (!caseId) {
      toaster({ message: "Case is required to save revisions.", success: false });
      return;
    }
    setIsSaving(true);
    try {
      const persisted = await persistComments();
      if (!persisted) {
        return;
      }
      // onClose awaits the guarded regeneration and toasts its outcome before navigating away
      onClose?.();
    } catch (error) {
      notifyRequestFailure({ message: "Failed to save revisions.", error });
    } finally {
      setIsSaving(false);
    }
  }, [caseId, persistComments, onClose, notifyRequestFailure]);

  const handleRegenerateSection = useCallback(
    async (sectionId) => {
      if (!caseId) {
        toaster({ message: "Case is required to regenerate revisions.", success: false });
        return;
      }
      dirtySectionIdsRef.current = { ...dirtySectionIdsRef.current, [sectionId]: true };
      regeneratedSectionIdsRef.current = { ...regeneratedSectionIdsRef.current, [sectionId]: true };
      setSectionUiStateById((prev) => ({ ...prev, [sectionId]: "loading" }));
      const persisted = await persistComments();
      if (!persisted) {
        dirtySectionIdsRef.current = { ...dirtySectionIdsRef.current, [sectionId]: false };
        setSectionUiStateById((prev) => ({ ...prev, [sectionId]: "idle" }));
        return;
      }
      const resolvedCommentId = getCommentIdForSection({
        sectionId,
        sectionDefinitions,
        editComments: persisted.updatedEditComments
      });
      console.log("resolved comment for section ", sectionId, " definition ", sectionDefinitions, "with comments ", persisted.updatedEditComments, '\n', resolvedCommentId)
      const resolvedCommentIds = resolvedCommentId ? [resolvedCommentId] : [];
      if (!resolvedCommentIds.length) {
        dirtySectionIdsRef.current = { ...dirtySectionIdsRef.current, [sectionId]: false };
        setSectionUiStateById((prev) => ({ ...prev, [sectionId]: "idle" }));
        toaster({ message: "Comment is required to regenerate this section.", success: false });
        return;
      }
      try {
        const suggestedResponse = await postPfqData(
          Constants.ApiUrl.createRequest,
          JSON.stringify(buildSuggestedEditPayload({ caseId, commentIds: resolvedCommentIds }))
        );
        if (!suggestedResponse?.success) {
          dirtySectionIdsRef.current = { ...dirtySectionIdsRef.current, [sectionId]: false };
          setSectionUiStateById((prev) => ({ ...prev, [sectionId]: "failed" }));
          notifyRequestFailure({
            message: suggestedResponse?.message || "Suggested edits request failed.",
          });
          return;
        }
        dirtySectionIdsRef.current = { ...dirtySectionIdsRef.current, [sectionId]: false };
        const sectionsWithNewIds = buildSectionComments(sectionDefinitions, persisted.updatedEditComments);
        await syncSectionUiStatesWithCreateRequests(sectionsWithNewIds);
      } catch (error) {
        dirtySectionIdsRef.current = { ...dirtySectionIdsRef.current, [sectionId]: false };
        setSectionUiStateById((prev) => ({ ...prev, [sectionId]: "failed" }));
        notifyRequestFailure({ message: "Suggested edits request failed.", error });
      }
    },
    [
      caseId,
      persistComments,
      sectionDefinitions,
      postPfqData,
      syncSectionUiStatesWithCreateRequests,
      notifyRequestFailure,
    ]
  );

  const handlePreviewFailure = useCallback((result) => {
    notifyRequestFailure({ message: result?.message || resolvedRegenerateConfig.previewFailureMessage });
  }, [resolvedRegenerateConfig, notifyRequestFailure]);

  const handlePreviewUpToDate = useCallback(() => {
    setShowPreview(true);
  }, []);

  const handlePreviewSuccess = useCallback(() => {
    toaster({ message: resolvedRegenerateConfig.successMessage, success: true });
    fetchCaseDetails();
    setShowPreview(true);
  }, [resolvedRegenerateConfig, fetchCaseDetails]);

  const applyPreviewStreamResult = useCallback((result) => {
    const outcomeHandlers = {
      failure: handlePreviewFailure,
      upToDate: handlePreviewUpToDate,
      success: handlePreviewSuccess,
    };
    outcomeHandlers[classifyPreviewStreamResult(result)](result);
  }, [handlePreviewFailure, handlePreviewUpToDate, handlePreviewSuccess]);

  const handlePreview = useCallback(async () => {
    if (!caseId) return;
    setIsSaving(true);
    try {
      const persisted = await persistComments();
      if (!persisted) {
        return;
      }
      const regenerationPromise = regenerate();
      claimOutcomeHandling(regenerationPromise);
      const result = await regenerationPromise;
      applyPreviewStreamResult(result);
    } finally {
      setIsSaving(false);
    }
  }, [caseId, persistComments, regenerate, claimOutcomeHandling, applyPreviewStreamResult]);

  const getSectionUiState = useCallback(
    (sectionId) => {
      return sectionUiStateById[sectionId] || "idle";
    },
    [sectionUiStateById]
  );

  const hasAnyComments = useMemo(
    () => sectionComments.some((section) => normalizeString(section.comment)),
    [sectionComments]
  );

  const isBusy = isLoading || isSaving;
  const isAnySectionRegenerating = useMemo(
    () => Object.values(sectionUiStateById).some((s) => s === "loading"),
    [sectionUiStateById]
  );

  const onEditPersisted = useCallback(async ()=>{
    await Promise.all([
      regenerateSilently(),
      fetchCaseDetails()
    ]);
  });

  return {
    sectionComments,
    sectionDefinitions,
    caseDetails,
    isInitialLoadPending,
    isLoading: isBusy,
    isSaving,
    isAnySectionRegenerating,
    updateSectionComment,
    clearAllComments,
    hasAnyComments,
    handleSave,
    handleRegenerateSection,
    handlePreview,
    showPreview,
    setShowPreview,
    getSectionUiState,
    suggestedEditsSettledSignal,
    resultRequests,
    applicableSectionIds,
    fetchCaseDetails,
    hasMoreHistory,
    loadOlderUntil,
    isRegenerating,
    regenerate,
    onEditPersisted,
    isFinalizingExit
  };
};

export default useDemandStudioData;
